import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Factory, ShieldCheck, AlertTriangle, Boxes } from "lucide-react";
import ConnectedAppsSummary from "@/components/dashboard/ConnectedAppsSummary";

const COLORS = ["#2563eb", "#0ea5e9", "#f59e0b", "#16a34a", "#dc2626"];

const STAGE_TONES = {
  machine_shop:     "bg-amber-50 border-amber-200 text-amber-800",
  impregnation:     "bg-teal-50 border-teal-200 text-teal-800",
  powder_coating:   "bg-purple-50 border-purple-200 text-purple-800",
  semi_finished:    "bg-indigo-50 border-indigo-200 text-indigo-800",
  assembly:         "bg-sky-50 border-sky-200 text-sky-800",
  testing:          "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800",
  final_inspection: "bg-orange-50 border-orange-200 text-orange-800",
  finished:         "bg-emerald-50 border-emerald-200 text-emerald-800",
  rework:           "bg-rose-50 border-rose-200 text-rose-800",
};
const stageTone = (k) => STAGE_TONES[k] || "bg-slate-50 border-slate-200 text-slate-700";

function KPI({ label, value, sub, icon: Icon, tone = "blue", tid }) {
  const tones = {
    blue: "text-blue-700 bg-blue-50",
    green: "text-emerald-700 bg-emerald-50",
    amber: "text-amber-700 bg-amber-50",
    red: "text-red-700 bg-red-50",
  };
  return (
    <div className="kpi-card" data-testid={tid}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{label}</div>
          <div className="mt-2 text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [wos, setWos] = useState([]);
  const [items, setItems] = useState([]);
  const [prodOverview, setProdOverview] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, w, i, p] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/work-orders"),
          api.get("/items"),
          api.get("/production-jobs/overview").catch(() => ({ data: null })),
        ]);
        setStats(s.data);
        setWos(w.data);
        setItems(i.data);
        setProdOverview(p.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  if (!stats) return <div className="text-slate-500 text-sm" data-testid="dashboard-loading">Loading metrics...</div>;

  const itemMap = Object.fromEntries(items.map((i) => [i.id, i]));
  const woBreakdown = Object.entries(stats.wo_breakdown).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));
  const defectBreakdown = Object.entries(stats.defect_breakdown).map(([k, v]) => ({ name: k, value: v }));
  const trend = stats.output_trend?.length ? stats.output_trend : [{ day: "—", qty: 0 }];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Overview</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Production Dashboard
        </h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Open Work Orders" value={stats.open_work_orders} sub={`${stats.done_work_orders} completed`} icon={Factory} tone="blue" tid="kpi-open-wo" />
        <KPI label="On-Time %" value={`${stats.on_time_pct}%`} sub="Trailing 30 days" icon={ShieldCheck} tone="green" tid="kpi-ontime" />
        <KPI label="Defect Rate" value={`${stats.defect_rate}%`} sub="Inspections failed" icon={AlertTriangle} tone="amber" tid="kpi-defect" />
        <KPI label="Low Stock SKUs" value={stats.low_stock_count} sub={`${stats.total_items} items total`} icon={Boxes} tone="red" tid="kpi-low-stock" />
      </div>

      {/* Live HR and Sales Reporter figures (viewer's own sign-in to each app) */}
      <ConnectedAppsSummary />

      {/* Production Overview — 8 stages + rework (Phase 19) */}
      {prodOverview && (
        <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="production-overview-card">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Production Overview</h3>
              <p className="text-xs text-slate-500 mt-0.5">Jobs currently sitting at each stage of the workflow.</p>
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-4">
              <span>Total Active: <span className="mono font-semibold text-slate-800" data-testid="prod-total-jobs">{prodOverview.total_active_jobs}</span></span>
              <span>Total WIP Qty: <span className="mono font-semibold text-slate-800" data-testid="prod-total-qty">{prodOverview.total_wip_qty}</span></span>
            </div>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2">
            {prodOverview.stages.map((s) => (
              <div key={s.key} className={`rounded-md border p-3 text-center ${stageTone(s.key)}`} data-testid={`prod-stage-${s.key}`}>
                <div className="text-[9px] uppercase tracking-[0.14em] font-semibold opacity-80">{s.label}</div>
                <div className="mono text-2xl font-extrabold mt-1" data-testid={`prod-stage-count-${s.key}`}>{s.job_count}</div>
                <div className="text-[10px] mono opacity-70 mt-0.5">{s.wip_qty} qty</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Recent Output Trend</h3>
            <span className="text-xs text-slate-500 uppercase tracking-[0.14em]">Qty per WO</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={trend}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#64748b", fontSize: 12 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                <Tooltip cursor={{ fill: "rgba(37,99,235,0.06)" }} />
                <Bar dataKey="qty" fill="#2563eb" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4" style={{ fontFamily: "Manrope" }}>Defect Severity</h3>
          {defectBreakdown.length === 0 ? (
            <div className="text-sm text-slate-500 h-64 flex items-center justify-center" data-testid="no-defects">
              No defects logged yet.
            </div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={defectBreakdown} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={2}>
                    {defectBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-800" style={{ fontFamily: "Manrope" }}>Recent Work Orders</h3>
          <span className="text-xs text-slate-500 uppercase tracking-[0.14em]">Latest 6</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 border-b border-slate-200">
                <th className="py-2 pr-4">WO</th>
                <th className="py-2 pr-4">Item</th>
                <th className="py-2 pr-4">Qty</th>
                <th className="py-2 pr-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {wos.slice(0, 6).map((w) => (
                <tr key={w.id} className="border-b border-slate-100" data-testid={`dashboard-wo-${w.code}`}>
                  <td className="py-3 pr-4 font-medium text-slate-800">{w.code}</td>
                  <td className="py-3 pr-4 text-slate-600">{itemMap[w.item_id]?.code} — {itemMap[w.item_id]?.name}</td>
                  <td className="py-3 pr-4 mono">{w.qty}</td>
                  <td className="py-3 pr-4">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] uppercase tracking-wider font-medium
                      ${w.status === "done" ? "bg-emerald-50 text-emerald-700" : w.status === "in_progress" ? "bg-blue-50 text-blue-700"
                        : w.status === "released" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                      {w.status.replace("_", " ")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
